import React, { useEffect, useState } from 'react';
import { AlleMandate } from '../components/portal/AlleMandate';
import { PortalSchnellstart } from '../components/portal/PortalSchnellstart';
import { PortalAblageStreifen } from '../components/portal/PortalAblage';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileSignature, Wallet, CalendarClock, MessageSquare, PhoneCall, ChevronRight, Loader2, CheckCircle2, FolderOpen, CreditCard, ScanLine, ListChecks } from 'lucide-react';
import Header from '../components/Header';
import { useAuth, api } from '../context/AuthContext';
import { MandantKontext } from '../components/MandantKontext';
import { FreigabenCard } from '../components/FreigabenCard';
import { PortalKopf, PortalSeite, eur, datum } from '../components/portal/PortalKopf';
import { SchliesszeitKarte } from '../components/SchliesszeitHinweis';

const ICON = { unterschrift: FileSignature, zahlung: Wallet, raten: CalendarClock, termin: CalendarClock, rechnung: Wallet, unterlage: ListChecks };
const SERVICES = [
  { key: 'upload', to: '/dokumente/scannen', icon: ScanLine, label: 'nav.services_items.upload.label' },
  { key: 'rueckruf', to: '/rueckruf', icon: PhoneCall, label: 'nav.services_items.rueckruf.label' },
  { key: 'raten', to: '/ratenzahlung', icon: CalendarClock, label: 'nav.services_items.raten.label' },
  { key: 'sepa', to: '/sepa-lastschrift', icon: CreditCard, label: 'nav.services_items.sepa.label' },
];

const Services = ({ t }) => (
  <section data-testid="portal-services">
    <h2 className="text-base md:text-lg font-serif text-[#1B3A5C] mb-3">{t('portal.services', 'Services')}</h2>
    <ul className="grid grid-cols-2 gap-2">
      {SERVICES.map((s) => (
        <li key={s.key}>
          <Link to={s.to} data-testid={`portal-service-${s.key}`} className="flex items-center gap-2 h-full p-3 bg-white rounded-2xl border border-gray-200 hover:border-[#b8893d]/70 transition-colors text-sm text-[#1B3A5C]">
            <s.icon className="w-4 h-4 text-[#b8893d] shrink-0" strokeWidth={1.75} /> {t(s.label)}
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

const Aufgabe = ({ a, index }) => {
  const Icon = ICON[a.typ] || Wallet;
  return (
    <li>
      <Link to={a.link} data-testid={`portal-todo-${a.typ}-${a.id || index}`} className="group flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-200 hover:border-[#b8893d]/70 transition-colors">
        <span className="w-10 h-10 rounded-[10px] bg-[#b8893d]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#b8893d]" strokeWidth={1.75} /></span>
        <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-[#1B3A5C]">{a.titel}</span>
          <span className="block text-xs text-gray-500 truncate">{[a.betrag != null && eur(a.betrag), a.sub, datum(a.datum)].filter(Boolean).join(' · ')}</span></span>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#b8893d]" />
      </Link>
    </li>
  );
};

const AktenStand = ({ akten, t }) => (
  <ul className="space-y-2">
    {akten.filter((a) => !a.abgeschlossen).slice(0, 5).map((a) => (
      <li key={a.case_id}>
        <Link to={`/portal/akten/${a.case_id}`} data-testid={`portal-start-akte-${a.case_id}`} className="block p-4 bg-white rounded-2xl border border-gray-200 hover:border-[#b8893d]/70 transition-colors">
          <span className="flex items-center gap-2 text-xs text-gray-500"><FolderOpen className="w-3.5 h-3.5 text-[#b8893d]" />{a.aktenzeichen || a.rechtsgebiet}{a.neu && <span className="ml-auto px-2 py-0.5 rounded-full bg-[#b8893d] text-[#0B1724] text-[10px] font-semibold">{t('portal.neu', 'neu')}</span>}</span>
          <span className="block mt-1 text-sm text-[#1B3A5C] line-clamp-2">{a.sachstand || t('portal.kein_sachstand', 'Noch kein Sachstand eingetragen.')}</span>
          {!!Object.values(a.unterlagen || {}).reduce((s, n) => s + n, 0) && <span className="block mt-1 text-xs text-[#56616D]" data-testid={`portal-start-unterlagen-${a.case_id}`}>Unterlagen: {a.unterlagen.offen || 0} offen · {a.unterlagen.pruefung || 0} in Prüfung · {a.unterlagen.erledigt || 0} vollständig</span>}
        </Link>
      </li>
    ))}
  </ul>
);

export default function PortalStart() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/portal/uebersicht', { params: { ansicht: 'start' } }).then((r) => setD(r.data)).catch(() => setD({ akten: [], zu_erledigen: [] })); }, []);
  const vorname = user?.name?.split(' ')[0] || '';
  return (
    <PortalSeite testId="portal-start">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-28">
        <PortalKopf kicker={t('portal.kicker', 'Mandantenportal')} titel={t('portal.hallo', { name: vorname, defaultValue: `Guten Tag${vorname ? `, ${vorname}` : ''}` })}>
          <div className="mt-3"><MandantKontext /></div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Link to="/meine-akten" data-testid="portal-nachricht-btn" className="inline-flex items-center justify-center gap-2 h-12 rounded-[10px] bg-white text-[#1B3A5C] text-sm font-medium hover:bg-[#f5f8fa] transition-colors"><MessageSquare className="w-4 h-4" />{t('portal.nachricht', 'Nachricht an die Kanzlei')}</Link>
            {d?.rueckruf_offen
              ? <span className="inline-flex items-center justify-center gap-2 h-12 rounded-[10px] border border-white/30 text-white/70 text-sm text-center px-2" data-testid="portal-rueckruf-offen"><PhoneCall className="w-4 h-4" />{t('portal.rueckruf_offen', 'Rückruf angefordert')}</span>
              : <Link to="/rueckruf" data-testid="portal-rueckruf-btn" className="inline-flex items-center justify-center gap-2 h-12 rounded-[10px] bg-gradient-to-b from-[#e0b56a] to-[#b8893d] text-[#0B1724] text-sm font-semibold hover:brightness-110 transition-[filter]"><PhoneCall className="w-4 h-4" />{t('portal.rueckruf', 'Rückruf anfordern')}</Link>}
            <Link to="/portal/termine" data-testid="portal-termine-btn" className="col-span-2 inline-flex items-center justify-center gap-2 h-11 rounded-[10px] border border-white/30 text-white text-sm hover:bg-white/10 transition-colors"><CalendarClock className="w-4 h-4" />Termine ansehen oder anfragen</Link>
          </div>
        </PortalKopf>
        <FreigabenCard />
        <div className="max-w-3xl mx-auto px-5 mt-8 space-y-8">
          <PortalSchnellstart />
          <PortalAblageStreifen text="Dokumente für die Kanzlei? Einfach Dateien auf eine beliebige Portalseite ziehen – sie werden sicher übermittelt." />
          <SchliesszeitKarte />
          <AlleMandate />
          {!d ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div> : <>
            <section data-testid="portal-zu-erledigen">
              <h2 className="text-base md:text-lg font-serif text-[#1B3A5C] mb-3">{t('portal.zu_erledigen', 'Zu erledigen')}</h2>
              {d.zu_erledigen.length ? <ul className="space-y-2">{d.zu_erledigen.map((a, i) => <Aufgabe key={a.id || i} a={a} index={i} />)}</ul>
                : <p className="flex items-center gap-2 p-4 bg-white rounded-2xl border border-gray-200 text-sm text-gray-500" data-testid="portal-nichts-zu-tun"><CheckCircle2 className="w-4 h-4 text-emerald-600" />{t('portal.nichts', 'Im Moment ist nichts zu tun.')}</p>}
            </section>
            {d.akten.length > 0 && (
              <section data-testid="portal-stand">
                <div className="flex items-end justify-between mb-3"><h2 className="text-base md:text-lg font-serif text-[#1B3A5C]">{t('portal.stand', 'Aktueller Stand Ihrer Akten')}</h2>
                  <Link to="/portal/akten" className="text-xs text-[#b8893d] hover:underline">{t('portal.alle_akten', 'Alle Akten')}</Link></div>
                <AktenStand akten={d.akten} t={t} />
              </section>
            )}
            <Services t={t} />
          </>}
        </div>
      </main>
    </PortalSeite>
  );
}
